import { useState, useEffect, useCallback, useRef } from 'react';
import { Trophy, Calendar, Play, Camera, MessageSquare, ChevronRight, Star, Gift, Sparkles, ChevronDown, ChevronUp, Crown, Award, Loader2, ArrowLeft, RefreshCw, Target } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, Link } from 'wouter';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { queryClient } from '@/lib/queryClient';
import { LoadingSpinner } from "@/components/ui/loading";
import Navbar from "@/components/navbar";

// Public winner interface based on API response
interface PublicWinner {
  id: number;
  firstName: string;
  lastName: string;
  prizeTitle: string;
  prizeValue: string;
  winDate: string;
  month: string;
  year: number;
  prizeImageUrl?: string;
  mediaUrl?: string;
  testimony?: string;
  mediaType?: 'photo' | 'video' | 'testimony';
  // 🏆 NEW: Claimed status fields
  prizeClaimed: boolean;
  claimedDate?: string | null;
  claimedDateFormatted?: string | null;
}

interface PublicWinnersResponse {
  winners: PublicWinner[];
  totalWinners: number;
  totalPrizeValue: string;
  timestamp: string;
  cached: boolean;
}

interface WinnersData {
  [month: string]: PublicWinner[];
}

// Challenge winner interface
interface ChallengeWinner {
  id: number;
  position: number;
  userId: number;
  challengeId: number;
  challengeName: string;
  challengeStatus: string;
  displayName: string;
  progressCount: number;
  referralCount: number;
  winningType: string;
  wonAt: string;
  prize: string | null;
}

interface ChallengeWinnersResponse {
  winners: ChallengeWinner[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Mobile winner card component
const MobileWinnerCard = ({ winner, onSelect }: { winner: PublicWinner; onSelect: (winner: PublicWinner) => void }) => (
  <div 
    onClick={() => onSelect(winner)}
    className="bg-white rounded-2xl shadow-lg overflow-hidden mb-4 active:scale-95 transition-transform cursor-pointer"
    data-testid={`card-winner-${winner.id}`}
  >
    <div className={`bg-gradient-to-r p-3 ${
      winner.prizeClaimed 
        ? 'from-green-600 to-green-700' 
        : 'from-blue-600 to-blue-700'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
            {winner.prizeClaimed ? (
              <Crown className="w-4 h-4 text-yellow-300" />
            ) : (
              <Trophy className="w-4 h-4 text-yellow-300" />
            )}
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <p className="text-white font-bold text-sm" data-testid={`text-winner-name-${winner.id}`}>
                {winner.firstName} {winner.lastName}
              </p>
              {winner.prizeClaimed && (
                <div className="bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full text-xs font-bold">
                  CLAIMED
                </div>
              )}
            </div>
            <p className="text-white/80 text-xs" data-testid={`text-win-date-${winner.id}`}>
              {winner.prizeClaimed && winner.claimedDateFormatted 
                ? `Claimed ${winner.claimedDateFormatted}`
                : `Won ${winner.winDate}`
              }
            </p>
          </div>
        </div>
        <div className="flex">
          {winner.prizeClaimed ? (
            <div className="flex flex-col items-center">
              <Sparkles className="w-4 h-4 text-yellow-300" />
              <span className="text-white/80 text-xs font-semibold">CLAIMED</span>
            </div>
          ) : (
            [...Array(5)].map((_, i) => (
              <Star key={i} className="w-3 h-3 text-yellow-300 fill-current" />
            ))
          )}
        </div>
      </div>
    </div>
    
    <div className="p-4">
      <div className="flex items-center space-x-3">
        <div className="w-20 h-20 bg-gray-200 rounded-xl shadow-md flex items-center justify-center">
          {winner.prizeImageUrl ? (
            <img 
              src={winner.prizeImageUrl} 
              alt={winner.prizeTitle}
              className="w-full h-full object-cover rounded-xl"
            />
          ) : (
            <Gift className="w-8 h-8 text-gray-400" />
          )}
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-gray-800 text-sm mb-1" data-testid={`text-prize-title-${winner.id}`}>
            {winner.prizeTitle}
          </h3>
          <div className="bg-gradient-to-r from-blue-100 to-blue-200 inline-flex px-2 py-1 rounded-full">
            <span className="text-blue-700 font-bold text-xs" data-testid={`text-prize-value-${winner.id}`}>
              {winner.prizeValue}
            </span>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-blue-500" />
      </div>
    </div>
  </div>
);

// Desktop winner card component
const DesktopWinnerCard = ({ winner }: { winner: PublicWinner }) => {
  const [selectedMediaType, setSelectedMediaType] = useState('photo');

  const renderMedia = () => {
    if (!winner.mediaUrl && !winner.testimony) return null;

    switch(selectedMediaType) {
      case 'video':
        return winner.mediaUrl && winner.mediaType === 'video' ? (
          <div className="relative aspect-video bg-gradient-to-br from-blue-900 to-blue-800 rounded-2xl overflow-hidden">
            <video 
              controls 
              className="w-full h-full"
              poster={winner.prizeImageUrl}
              data-testid={`video-winner-${winner.id}`}
            >
              <source src={winner.mediaUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        ) : null;
      case 'photo':
        return winner.mediaUrl && winner.mediaType === 'photo' ? (
          <div className="relative aspect-video rounded-2xl overflow-hidden shadow-xl">
            <img 
              src={winner.mediaUrl} 
              alt={`${winner.firstName}'s winning moment`}
              className="w-full h-full object-cover"
              data-testid={`img-winner-photo-${winner.id}`}
            />
          </div>
        ) : null;
      case 'testimony':
        return winner.testimony ? (
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 p-6 rounded-2xl">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
              </div>
              <div>
                <p className="text-gray-700 leading-relaxed italic" data-testid={`text-testimony-${winner.id}`}>
                  "{winner.testimony}"
                </p>
                <p className="mt-3 text-sm font-semibold text-blue-600">
                  — {winner.firstName} {winner.lastName}
                </p>
              </div>
            </div>
          </div>
        ) : null;
      default:
        return null;
    }
  };

  const availableMediaTypes = [];
  if (winner.mediaUrl && winner.mediaType === 'video') availableMediaTypes.push('video');
  if (winner.mediaUrl && winner.mediaType === 'photo') availableMediaTypes.push('photo');
  if (winner.testimony) availableMediaTypes.push('testimony');

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 border border-blue-100" data-testid={`card-desktop-winner-${winner.id}`}>
      {/* Winner Header with Gradient */}
      <div className={`bg-gradient-to-r p-6 ${
        winner.prizeClaimed 
          ? 'from-green-600 via-green-500 to-green-700' 
          : 'from-blue-600 via-blue-500 to-blue-700'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
              {winner.prizeClaimed ? (
                <Crown className="w-6 h-6 text-green-600" />
              ) : (
                <Trophy className="w-6 h-6 text-blue-600" />
              )}
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <h3 className="text-xl font-bold text-white" data-testid={`text-desktop-winner-name-${winner.id}`}>
                  {winner.firstName} {winner.lastName}
                </h3>
                {winner.prizeClaimed && (
                  <div className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-bold flex items-center space-x-1">
                    <Award className="w-4 h-4" />
                    <span>PRIZE CLAIMED</span>
                  </div>
                )}
              </div>
              <p className="text-blue-100 text-sm flex items-center">
                {winner.prizeClaimed ? (
                  <>
                    <Sparkles className="w-3 h-3 mr-1" />
                    Prize claimed on {winner.claimedDateFormatted || winner.winDate}
                  </>
                ) : (
                  <>
                    <Award className="w-3 h-3 mr-1" />
                    Won on {winner.winDate}
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 text-yellow-300 fill-current" />
            ))}
          </div>
        </div>
      </div>

      {/* Prize Information */}
      <div className="p-6">
        <div className="flex items-start space-x-4 mb-6">
          <div className="relative">
            <div className="w-28 h-28 bg-gray-200 rounded-xl shadow-lg flex items-center justify-center">
              {winner.prizeImageUrl ? (
                <img 
                  src={winner.prizeImageUrl} 
                  alt={winner.prizeTitle}
                  className="w-full h-full object-cover rounded-xl"
                />
              ) : (
                <Gift className="w-12 h-12 text-gray-400" />
              )}
            </div>
            <div className="absolute -top-2 -right-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full">
              WON!
            </div>
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-bold text-gray-800 mb-1" data-testid={`text-desktop-prize-title-${winner.id}`}>
              {winner.prizeTitle}
            </h4>
            <div className="bg-gradient-to-r from-blue-100 to-blue-200 inline-flex px-3 py-1 rounded-full">
              <span className="text-blue-700 font-bold text-sm" data-testid={`text-desktop-prize-value-${winner.id}`}>
                Value: {winner.prizeValue}
              </span>
            </div>
          </div>
        </div>

        {/* Media Section */}
        {availableMediaTypes.length > 0 && (
          <div>
            {/* Media Type Tabs */}
            <div className="flex space-x-2 mb-4">
              {availableMediaTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedMediaType(type)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                    selectedMediaType === type
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`button-media-${type}-${winner.id}`}
                >
                  {type === 'video' && <Play className="w-4 h-4" />}
                  {type === 'photo' && <Camera className="w-4 h-4" />}
                  {type === 'testimony' && <MessageSquare className="w-4 h-4" />}
                  <span className="capitalize">{type}</span>
                </button>
              ))}
            </div>

            {/* Media Content */}
            <div className="mb-4">
              {renderMedia()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Challenge Winner Card Component
const ChallengeWinnerCard = ({ winner }: { winner: ChallengeWinner }) => {
  const wonDate = new Date(winner.wonAt).toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 border border-purple-100" data-testid={`card-challenge-winner-${winner.id}`}>
      <div className="bg-gradient-to-r from-purple-600 via-purple-500 to-purple-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
              <Target className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white" data-testid={`text-challenge-winner-name-${winner.id}`}>
                {winner.displayName}
              </h3>
              <p className="text-purple-100 text-sm flex items-center">
                <Award className="w-3 h-3 mr-1" />
                First to reach target on {wonDate}
              </p>
            </div>
          </div>
          <div className="bg-yellow-400 text-purple-900 px-4 py-2 rounded-full text-sm font-bold flex items-center space-x-1">
            <Crown className="w-4 h-4" />
            <span>1st PLACE</span>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="mb-4">
          <h4 className="text-lg font-bold text-gray-800 mb-2" data-testid={`text-challenge-name-${winner.id}`}>
            {winner.challengeName}
          </h4>
          <div className="flex items-center space-x-4">
            <div className="bg-purple-100 px-3 py-1 rounded-full">
              <span className="text-purple-700 font-semibold text-sm">
                Target Reached: {winner.progressCount}
              </span>
            </div>
            {winner.referralCount > 0 && (
              <div className="bg-blue-100 px-3 py-1 rounded-full">
                <span className="text-blue-700 font-semibold text-sm">
                  {winner.referralCount} Referrals
                </span>
              </div>
            )}
          </div>
        </div>

        {winner.prize && (
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl">
            <div className="flex items-center space-x-2">
              <Gift className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-xs text-purple-600 font-semibold">PRIZE WON</p>
                <p className="text-gray-800 font-bold" data-testid={`text-challenge-prize-${winner.id}`}>
                  {winner.prize}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Winner detail modal for mobile
const WinnerDetailModal = ({ winner, onClose }: { winner: PublicWinner | null; onClose: () => void }) => {
  const [selectedMedia, setSelectedMedia] = useState('photo');

  if (!winner) return null;

  return (
    <Dialog open={!!winner} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Winner Details</DialogTitle>
          <DialogDescription>
            Celebration of our latest winner
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Winner Info */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gray-200 rounded-xl flex items-center justify-center">
                {winner.prizeImageUrl ? (
                  <img 
                    src={winner.prizeImageUrl} 
                    alt={winner.prizeTitle}
                    className="w-full h-full object-cover rounded-xl"
                  />
                ) : (
                  <Gift className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div>
                <div className="flex items-center space-x-1 mb-1">
                  <Gift className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-blue-600 font-semibold">PRIZE WON</span>
                </div>
                <h3 className="font-bold text-gray-800" data-testid={`text-modal-prize-title-${winner.id}`}>
                  {winner.prizeTitle}
                </h3>
                <div className="bg-white px-3 py-1 rounded-full inline-flex mt-2">
                  <span className="text-blue-700 font-bold text-sm" data-testid={`text-modal-prize-value-${winner.id}`}>
                    {winner.prizeValue}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Media Display */}
          {winner.mediaUrl && (
            <div className="aspect-video bg-gray-200 rounded-2xl overflow-hidden">
              {winner.mediaType === 'video' ? (
                <video controls className="w-full h-full" data-testid={`video-modal-${winner.id}`}>
                  <source src={winner.mediaUrl} type="video/mp4" />
                </video>
              ) : (
                <img 
                  src={winner.mediaUrl} 
                  alt="Winner media"
                  className="w-full h-full object-cover"
                  data-testid={`img-modal-${winner.id}`}
                />
              )}
            </div>
          )}

          {/* Testimony */}
          {winner.testimony && (
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-2xl">
              <MessageSquare className="w-6 h-6 text-blue-600 mb-2" />
              <p className="text-gray-700 italic" data-testid={`text-modal-testimony-${winner.id}`}>
                "{winner.testimony}"
              </p>
              <p className="mt-2 text-sm font-semibold text-blue-600">
                — {winner.firstName}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function WinnersPage() {
  const [expandedMonths, setExpandedMonths] = useState<Record<string, boolean>>({});
  const [selectedWinner, setSelectedWinner] = useState<PublicWinner | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [, setLocation] = useLocation();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>('');
  const pollingIntervalRef = useRef<number | null>(null);

  // Detect mobile screen size
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Smart polling logic - determines if we should poll based on server indicators
  const shouldPoll = useCallback((data: PublicWinnersResponse | undefined) => {
    if (!data) return false;
    
    // Poll if data is not from cache (indicates fresh fetch from backend)
    if (!data.cached) return true;
    
    // Poll if we haven't checked for updates recently
    const lastUpdate = new Date(data.timestamp).getTime();
    const now = Date.now();
    const timeSinceUpdate = now - lastUpdate;
    
    // Poll more frequently if data is fresh (within 2 minutes)
    return timeSinceUpdate < 120000; // 2 minutes
  }, []);

  // Manual refresh function
  const handleManualRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ['/api/public/winners'] });
      console.log('[WINNERS] Manual refresh triggered');
    } catch (error) {
      console.error('[WINNERS] Manual refresh failed:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // Fetch winners data with intelligent polling
  const { data: winnersData, isLoading, error, refetch } = useQuery({
    queryKey: ['/api/public/winners'],
    queryFn: async () => {
      const response = await fetch('/api/public/winners');
      if (!response.ok) {
        throw new Error('Failed to fetch winners');
      }
      const data = await response.json();
      
      // Update last update time for display
      setLastUpdateTime(data.timestamp);
      
      console.log(`[WINNERS] Data fetched - Cached: ${data.cached}, Winners: ${data.totalWinners}`);
      return data;
    },
    refetchInterval: (data) => {
      if (!data) return false;
      
      // If data indicates recent activity, poll every 10 seconds
      if (shouldPoll(data as unknown as PublicWinnersResponse)) {
        console.log('[WINNERS] Active polling mode - 10 second intervals');
        return 10000; // 10 seconds
      }
      
      // Normal polling every 2 minutes for less frequent updates
      console.log('[WINNERS] Normal polling mode - 2 minute intervals');
      return 120000; // 2 minutes
    },
    refetchIntervalInBackground: true,
    staleTime: 0, // Always consider data potentially stale for responsiveness
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch challenge winners - only position 1 from ended challenges
  const { data: challengeWinnersData, isLoading: challengeWinnersLoading } = useQuery<ChallengeWinnersResponse>({
    queryKey: ['/api/v1/challenges/winners/all'],
    queryFn: async () => {
      const response = await fetch('/api/v1/challenges/winners/all?page=1&pageSize=100');
      if (!response.ok) {
        throw new Error('Failed to fetch challenge winners');
      }
      return response.json();
    },
    staleTime: 60000, // 1 minute
  });

  const toggleMonth = (month: string) => {
    setExpandedMonths(prev => ({
      ...prev,
      [month]: !prev[month]
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100">
        <Navbar />
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 60px)' }}>
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600">Loading our amazing winners...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100">
        <Navbar />
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 60px)' }}>
          <div className="text-center">
            <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Unable to Load Winners</h2>
            <p className="text-gray-600">Please try again later</p>
          </div>
        </div>
      </div>
    );
  }

  const winners: PublicWinner[] = winnersData?.winners || [];
  const totalWinners = winnersData?.totalWinners || 0;
  const totalPrizeValue = winnersData?.totalPrizeValue || 'R0';

  // Group winners by month for desktop view
  const winnersByMonth: WinnersData = winners.reduce((acc, winner) => {
    // Check if month already includes the year to avoid duplication
    const monthKey = winner.month.includes(String(winner.year)) 
      ? winner.month 
      : `${winner.month} ${winner.year}`;
    if (!acc[monthKey]) {
      acc[monthKey] = [];
    }
    acc[monthKey].push(winner);
    return acc;
  }, {} as WinnersData);

  // Filter challenge winners to show only position 1 from ended challenges
  const firstPlaceChallengeWinners = challengeWinnersData?.winners.filter(
    winner => winner.position === 1 && winner.challengeStatus === 'ended'
  ) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100">
      {/* Navbar */}
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-blue-500 to-blue-700">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white opacity-10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white opacity-10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-full">
                <Trophy className="w-12 h-12 text-yellow-300" />
              </div>
            </div>
            <h1 className="text-5xl font-bold text-white mb-4" data-testid="title-winners-page">
              Our Lucky Winners
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto" data-testid="text-winners-description">
              Real people, real prizes, real excitement! See who's been winning amazing prizes with PrizeDrop
            </p>
            {/* Data freshness and refresh controls */}
            <div className="mt-6 flex justify-center items-center space-x-4">
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <div className={`w-2 h-2 rounded-full animate-pulse ${
                  winnersData?.cached === false 
                    ? 'bg-green-400' 
                    : shouldPoll(winnersData) 
                    ? 'bg-yellow-400' 
                    : 'bg-white/60'
                }`}></div>
                <span className="text-white/90 text-sm font-medium">
                  {winnersData?.cached === false 
                    ? 'Live Data' 
                    : shouldPoll(winnersData) 
                    ? 'Active Updates' 
                    : 'Cached Data'
                  }
                </span>
              </div>
              
              <button
                onClick={handleManualRefresh}
                disabled={isRefreshing}
                className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 px-4 py-2 rounded-full transition-all disabled:opacity-50"
                data-testid="button-manual-refresh"
              >
                <RefreshCw className={`w-4 h-4 text-white ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="text-white text-sm font-medium">
                  {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </span>
              </button>
              
              {lastUpdateTime && (
                <div className="text-white/70 text-xs">
                  Updated: {new Date(lastUpdateTime).toLocaleTimeString()}
                </div>
              )}
            </div>
            
            <div className="mt-8 flex justify-center space-x-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-white" data-testid="stat-total-winners">{totalWinners}</div>
                <div className="text-white/80 text-sm">Total Winners</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white" data-testid="stat-total-value">{totalPrizeValue}</div>
                <div className="text-white/80 text-sm">Prizes Given</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white" data-testid="stat-happiness">100%</div>
                <div className="text-white/80 text-sm">Happy Winners</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Winners Section with Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs defaultValue="competitions" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8" data-testid="tabs-winners">
            <TabsTrigger value="competitions" data-testid="tab-competitions">
              <Trophy className="w-4 h-4 mr-2" />
              Competitions
            </TabsTrigger>
            <TabsTrigger value="challenges" data-testid="tab-challenges">
              <Target className="w-4 h-4 mr-2" />
              Challenge Winners
            </TabsTrigger>
          </TabsList>

          {/* Competitions Tab */}
          <TabsContent value="competitions">
            {isMobile ? (
              // Mobile View - Competitions
              <>
                <div className="mb-6 text-center">
                  <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-100 to-blue-200 px-4 py-2 rounded-full mb-4">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                    <span className="text-blue-700 font-semibold">Recent Winners</span>
                    <div className={`w-2 h-2 rounded-full ml-2 ${
                      winnersData?.cached === false 
                        ? 'bg-green-500 animate-pulse' 
                        : shouldPoll(winnersData) 
                        ? 'bg-yellow-500 animate-pulse' 
                        : 'bg-gray-400'
                    }`} data-testid="indicator-mobile-data-status"></div>
                  </div>
                </div>

                {winners.map((winner) => (
                  <MobileWinnerCard 
                    key={winner.id} 
                    winner={winner} 
                    onSelect={setSelectedWinner}
                  />
                ))}

                <WinnerDetailModal 
                  winner={selectedWinner} 
                  onClose={() => setSelectedWinner(null)}
                />
              </>
            ) : (
              // Desktop View - Competitions
              <>
                <div className="mb-12 text-center">
                  <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-100 to-blue-200 px-4 py-2 rounded-full mb-4">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                    <span className="text-blue-700 font-semibold">Latest Winners</span>
                    <div className={`w-2 h-2 rounded-full ml-2 ${
                      winnersData?.cached === false 
                        ? 'bg-green-500 animate-pulse' 
                        : shouldPoll(winnersData) 
                        ? 'bg-yellow-500 animate-pulse' 
                        : 'bg-gray-400'
                    }`} data-testid="indicator-desktop-data-status"></div>
                  </div>
                  
                  {/* Desktop refresh controls */}
                  <div className="flex justify-center items-center space-x-4 mb-4">
                    <button
                      onClick={handleManualRefresh}
                      disabled={isRefreshing}
                      className="flex items-center space-x-2 bg-white hover:bg-gray-50 border border-gray-200 px-4 py-2 rounded-full shadow-sm transition-all disabled:opacity-50"
                      data-testid="button-desktop-manual-refresh"
                    >
                      <RefreshCw className={`w-4 h-4 text-blue-600 ${isRefreshing ? 'animate-spin' : ''}`} />
                      <span className="text-blue-600 text-sm font-medium">
                        {isRefreshing ? 'Refreshing...' : 'Refresh Winners'}
                      </span>
                    </button>
                    
                    <div className="text-gray-500 text-sm">
                      {winnersData?.cached === false && 'Live data'}
                      {winnersData?.cached && shouldPoll(winnersData) && 'Checking for updates...'}
                      {winnersData?.cached && !shouldPoll(winnersData) && 'Cached data'}
                      {lastUpdateTime && (
                        <span className="ml-2">• Updated {new Date(lastUpdateTime).toLocaleTimeString()}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Monthly Winners */}
                {Object.entries(winnersByMonth).map(([month, monthWinners]) => (
                  <div key={month} className="mb-8">
                    {/* Month Header */}
                    <button
                      onClick={() => toggleMonth(month)}
                      className="w-full group"
                      data-testid={`button-toggle-month-${month.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-blue-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl">
                              <Calendar className="w-6 h-6 text-white" />
                            </div>
                            <div className="text-left">
                              <h2 className="text-2xl font-bold text-gray-800" data-testid={`text-month-${month.replace(/\s+/g, '-').toLowerCase()}`}>{month}</h2>
                              <p className="text-blue-600 text-sm font-medium">
                                {monthWinners.length} {monthWinners.length === 1 ? 'Winner' : 'Winners'}
                              </p>
                            </div>
                          </div>
                          <div className="bg-blue-100 group-hover:bg-blue-200 p-2 rounded-full transition-colors">
                            {expandedMonths[month] ? (
                              <ChevronUp className="w-5 h-5 text-blue-600" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-blue-600" />
                            )}
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* Winners Cards */}
                    {expandedMonths[month] && (
                      <div className="mt-6 grid gap-6 md:grid-cols-2 animate-fadeIn">
                        {monthWinners.map((winner) => (
                          <DesktopWinnerCard key={winner.id} winner={winner} />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </TabsContent>

          {/* Challenge Winners Tab */}
          <TabsContent value="challenges">
            {challengeWinnersLoading ? (
              <div className="text-center py-12">
                <LoadingSpinner size="lg" />
                <p className="mt-4 text-gray-600">Loading challenge winners...</p>
              </div>
            ) : firstPlaceChallengeWinners.length === 0 ? (
              <div className="text-center py-12">
                <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-800 mb-2">No Challenge Winners Yet</h2>
                <p className="text-gray-600">Challenge winners will appear here when challenges are completed</p>
              </div>
            ) : (
              <>
                <div className="mb-8 text-center">
                  <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-100 to-purple-200 px-4 py-2 rounded-full">
                    <Crown className="w-5 h-5 text-purple-600" />
                    <span className="text-purple-700 font-semibold">First to Reach Target</span>
                  </div>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  {firstPlaceChallengeWinners.map((winner) => (
                    <ChallengeWinnerCard key={winner.id} winner={winner} />
                  ))}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* CTA Section - Show for both tabs */}
        <div className="mt-12 text-center">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 shadow-xl">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Be Our Next Winner?
            </h2>
            <p className="text-white/90 mb-6 max-w-md mx-auto">
              Join thousands of players and enter daily competitions for your chance to win amazing prizes!
            </p>
            <Link to="/competitions">
              <button 
                className="bg-white text-blue-600 font-bold px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105"
                data-testid="button-start-playing"
              >
                Start Playing Today
              </button>
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}